import dayjs, { Dayjs } from 'dayjs';
import React, { useMemo, useState, useEffect } from 'react';

import { Box } from '@mui/system';
import { DatePicker } from '@mui/x-date-pickers-pro';

import ElementLabel from './ElementLabel';
import { ElementProps } from '../../../@types/forms/types';
import './elements-vanilla-mobile/address-book-mobile.scss';

//------------------------------------------------------------------

type DateElementProps = {
  el: ElementProps;
  pageNumberScale: number;
  isFormFillerPage: boolean;
  elementContentValue?: Dayjs | null;
  elementContentValueString?: string;
  handleDateChange?: (el: ElementProps, newValue: dayjs.Dayjs | null) => void;
  collaboratorColor?: string;
  disabled?: boolean;
  errorMessage?: string;
};

const DateElement: React.FC<DateElementProps> = ({
  el,
  pageNumberScale,
  isFormFillerPage,
  elementContentValue,
  elementContentValueString,
  handleDateChange,
  collaboratorColor,
  disabled = false,
  errorMessage,
}) => {
  const [value, setValue] = useState<string>('');
  const [isOpen, setIsOpen] = useState(false);

  const toISOorEmpty = (s?: string | null) => {
    if (!s) return '';
    const t = String(s).trim();
    if (!t) return '';
    const d = dayjs(t);
    return d.isValid() ? d.format('YYYY-MM-DD') : '';
  };

  const pickerValue = useMemo<Dayjs | null>(() => (value ? dayjs(value) : null), [value]);

  useEffect(() => {
    if (el.todayDate) {
      const todayISO = dayjs().format('YYYY-MM-DD');
      setValue(todayISO);
      if (!elementContentValueString) {
        handleDateChange?.(el, dayjs(todayISO));
      }
    } else {
      setValue(toISOorEmpty(elementContentValueString));
    }
  }, [el.todayDate, elementContentValueString]);

  return (
    <>
      {isFormFillerPage && (
        <Box
          sx={{
            fontSize: `${el.fontSize * pageNumberScale}px`,
            width: el.width * pageNumberScale,
            backgroundColor: !el.todayDate ? collaboratorColor : undefined,
            ...(!collaboratorColor &&
              !el.todayDate && {
                borderRadius: '8px',
                padding: '8px 8px 0',
                width: '100%',
              }),
          }}
          className={el.todayDate || collaboratorColor ? '' : 'date-range-bg'}
        >
          <DatePicker
            disabled={el.isChild || el.todayDate || disabled}
            open={el.todayDate ? false : isOpen}
            disablePast={el.disablePast}
            slotProps={{
              textField: {
                variant: 'standard',
                size: 'small',
                fullWidth: true,
                error: !!errorMessage,
                helperText: errorMessage || null,

                sx: {
                  '& .MuiPickersInputBase-sectionsContainer': {
                    fontFamily: el.font,
                    fontWeight: el.textStyle.includes('Bold') ? 'bold' : 'normal',
                    fontStyle: el.textStyle.includes('Italic') ? 'italic' : 'normal',
                    textDecoration: el.textDecoration === 1 ? 'underline' : 'none',
                    color: el.color,
                    fontSize: `${el.fontSize * pageNumberScale}px`,
                    lineHeight: `${el.fontSize * pageNumberScale * 1.2}px`,
                    justifyContent:
                      el.textAlignment === 1
                        ? 'center'
                        : el.textAlignment === 2
                          ? 'flex-end'
                          : 'flex-start',
                  },
                },

                onClick: () => setIsOpen(true),

                slotProps: {
                  htmlInput: {
                    placeholder: 'YYYY-MM-DD',
                    autoComplete: 'off',
                    name: `${el.id}-date`,
                  },
                },
              },
            }}
            slots={{ openPickerButton: () => null }}
            value={pickerValue}
            onChange={(newValue) => {
              const formatted = newValue ? newValue.format('YYYY-MM-DD') : '';
              setValue(formatted);
              handleDateChange?.(el, newValue);
            }}
            onClose={() => setIsOpen(false)}
            onAccept={() => setIsOpen(false)}
            closeOnSelect
          />
        </Box>
      )}
      {!isFormFillerPage && (
        <ElementLabel
          el={el}
          label="YYYY-MM-DD"
          pageNumberScale={pageNumberScale}
          collaboratorColor={collaboratorColor}
        />
      )}
    </>
  );
};

export default DateElement;
